import React, { useEffect, useRef } from "react";

import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";

import { Icon } from "@/components/icons/Icon";
import { twMerge } from "@/utils/twMerge";

import { useCreateHintHoverHandlers } from "../hints/useHintHover";

import {
  MENU_ITEM_BUTTON_CLASS,
  MenuItemContent,
  MenuSeparator,
  MenuSectionTitle,
} from "./MenuItemContent";

import { Sidebar } from ".";

import type { HintKey } from "@/store/HintsStore";
import type { MouseEventHandler, ComponentProps } from "react";

type MenuRenderPropArg = {
  open: boolean;
  close: () => void;
};

export type DropdownComponentItem = {
  component: React.ReactNode | ((renderArgs: MenuRenderPropArg) => React.ReactNode);
  disabled?: boolean;
  footer?: React.ComponentType<{ open: boolean; close: () => void }>;
  header?: React.ComponentType<{ open: boolean; close: () => void }>;
};

export type DropdownItemRenderProps = React.ComponentPropsWithRef<"button">;

export type DropdownRenderItem = {
  renderItem: (props: DropdownItemRenderProps) => React.ReactNode;
  disabled?: boolean;
  footer?: React.ComponentType<{ open: boolean; close: () => void }>;
  header?: React.ComponentType<{ open: boolean; close: () => void }>;
};

export type DropdownActionItem = {
  title: string;
  title2?: React.ReactNode;
  caption?: string;
  caption2?: string;
  superscript?: string;
  hotkey?: React.ReactNode;
  testId?: string;
  icon?: React.ReactNode;
  onClick?: MouseEventHandler<HTMLButtonElement>;
  disabled?: boolean;
  active?: boolean;
  /**
   * An on/off switch rather than one of the choices: `active` reads as pressed, and in a `radio`
   * popover the item sits after the radio group instead of inside it
   */
  toggle?: boolean;
  footer?: React.ComponentType<{ open: boolean; close: () => void }>;
  header?: React.ComponentType<{ open: boolean; close: () => void }>;
  hintKey?: HintKey;
  className?: string;
};

export type DropdownItem =
  | DropdownComponentItem
  | DropdownRenderItem
  | DropdownActionItem
  | {
      type: "separator";
    }
  | {
      type: "title";
      title: string;
    };

/** Props for the button that opens the menu, beyond its label. */
export type DropdownToggleButtonProps = Pick<
  React.ComponentPropsWithRef<"button">,
  "ref" | "tabIndex" | "aria-description"
> &
  Record<`data-${string}`, string>;

type DropdownBaseProps = {
  id?: string;
  items?: DropdownItem[];
  portal?: boolean;
  className?: string;
  itemsClassName?: string;
  anchor?: ComponentProps<typeof MenuItems>["anchor"];
  toggleButtonLabel?: string;
  /** Extra props for the toggle button, e.g. to take it out of the Tab order */
  toggleButtonProps?: DropdownToggleButtonProps;
  modal?: boolean;
  header?: React.ComponentType<{ open: boolean; close: () => void }>;
  footer?: React.ComponentType<{ open: boolean; close: () => void }>;
  children?: React.ReactNode;
  onOpenChange?: (open: boolean) => void;
  closeRef?: React.RefObject<(() => void) | null>;
  disabled?: boolean;
  panelDataAttributes?: Record<string, string>;
  /** `menuitemradio`: the items are a single choice, the active one checked */
  itemsRole?: "menuitemradio";
};

type DropdownWithToggleComponent = {
  toggleButtonComponent: React.ReactNode;
  toggleText?: never;
  toggleTextColor?: never;
  toggleTextInvertedBg?: never;
  toggleTextLabel?: never;
};

type DropdownWithToggleText = {
  toggleText: string;
  toggleTextColor?: string;
  toggleTextInvertedBg?: boolean;
  toggleButtonComponent?: never;
  toggleTextLabel?: string;
};

export type DropdownProps = DropdownBaseProps &
  (DropdownWithToggleComponent | DropdownWithToggleText);

type OnOpenChangeHandler = (open: boolean) => void;

function MenuEventHandler({
  open,
  onOpenChange,
}: {
  open: boolean;
  onOpenChange?: OnOpenChangeHandler;
}) {
  const ref = useRef(open);

  useEffect(() => {
    if (open !== ref.current) {
      ref.current = open;
      onOpenChange?.(open);
    }
  }, [open, onOpenChange]);

  return null;
}

// TODO: refactor this as a modular component

const ToggleButton = ({
  title,
  toggleTextColor,
  invertedBg,
  toggleTextLabel,
  disabled,
}: {
  title: string;
  toggleTextLabel?: string;
  toggleTextColor?: string;
  invertedBg?: boolean;
  disabled?: boolean;
}) => {
  return (
    <Sidebar.Button
      as="div"
      aria-label={toggleTextLabel || title}
      className={twMerge(
        "text-gray-500 text-[15px] leading-[1.2]",
        "sm:text-[17px]",
        toggleTextColor,
        disabled && "opacity-40",
      )}
      inverted={invertedBg}
      disabled={disabled}
    >
      <div className="ml-1 mr-1.5">{title}</div>
      <Icon
        name="ArrowDownLarge"
        width={16}
        height={16}
        className="ml-auto [&>path]:stroke-document-text"
      />
    </Sidebar.Button>
  );
};

export function Dropdown({
  id,
  items,
  portal,
  toggleButtonComponent,
  toggleText,
  toggleTextColor,
  toggleTextInvertedBg,
  toggleTextLabel,
  className,
  itemsClassName,
  anchor = { to: "bottom start", padding: "10px" },
  toggleButtonLabel,
  toggleButtonProps,
  modal,
  header: Header,
  footer: Footer,
  children,
  onOpenChange,
  closeRef,
  disabled,
  panelDataAttributes,
  itemsRole,
}: DropdownProps) {
  const createHintHandlers = useCreateHintHoverHandlers();
  const setItemRole = itemsRole
    ? (item: HTMLButtonElement | null) => item?.setAttribute("role", itemsRole)
    : undefined;

  return (
    <Menu>
      {({ open, close }) => {
        if (closeRef) closeRef.current = close;
        return (
          <div className="contents">
            <MenuEventHandler open={open} onOpenChange={onOpenChange} />
            <MenuButton
              {...toggleButtonProps}
              id={id}
              className={className}
              aria-label={toggleButtonLabel}
              disabled={disabled}
            >
              {toggleButtonComponent ??
                (toggleText && (
                  <ToggleButton
                    title={toggleText}
                    toggleTextColor={toggleTextColor}
                    toggleTextLabel={toggleTextLabel}
                    invertedBg={toggleTextInvertedBg}
                    disabled={disabled}
                  />
                ))}
            </MenuButton>
            {open ? (
              <MenuItems
                {...panelDataAttributes}
                modal={modal}
                portal={portal}
                anchor={anchor}
                className={twMerge(
                  "relative min-w-[250px] p-1.5 rounded-12 bg-block-bg shadow-md dark:shadow-block-inverted z-100",
                  // TODO: adjust scrollbars in rounded containers
                  // (Chrome does not round container on a scrollbar side)
                  "no-scrollbar outline-none",
                  itemsClassName,
                )}
              >
                {Header && <Header open={open} close={close} />}
                {items?.map((item, index) => {
                  if ("type" in item) {
                    if (item.type === "separator") {
                      const key = `separator${index}`;
                      return (
                        <MenuItem key={key} disabled>
                          <MenuSeparator />
                        </MenuItem>
                      );
                    }

                    if (item.type === "title") {
                      const key = `title${index}`;
                      return (
                        <MenuItem key={key} disabled>
                          <MenuSectionTitle title={item.title} />
                        </MenuItem>
                      );
                    }

                    return null;
                  }

                  const { footer: ItemFooter, header: ItemHeader } = item;

                  if ("component" in item) {
                    const key = `custom${index}`;

                    return (
                      <React.Fragment key={key}>
                        {ItemHeader && <ItemHeader open={open} close={close} />}
                        <MenuItem disabled={item.disabled}>
                          {typeof item.component === "function"
                            ? item.component({ open, close })
                            : item.component}
                        </MenuItem>
                        {ItemFooter && <ItemFooter open={open} close={close} />}
                      </React.Fragment>
                    );
                  }

                  if ("renderItem" in item) {
                    const key = `render${index}`;

                    // Headless UI's `MenuItem` merges the item props/ref onto the returned
                    // element, so `renderItem` receives no props here.
                    return (
                      <React.Fragment key={key}>
                        {ItemHeader && <ItemHeader open={open} close={close} />}
                        <MenuItem disabled={item.disabled}>{item.renderItem({})}</MenuItem>
                        {ItemFooter && <ItemFooter open={open} close={close} />}
                      </React.Fragment>
                    );
                  }

                  const key = `${item.title}${index}`;

                  return (
                    <React.Fragment key={key}>
                      {ItemHeader && <ItemHeader open={open} close={close} />}
                      <MenuItem disabled={item.disabled}>
                        <button
                          data-testid={item.testId}
                          type="button"
                          aria-label={item.title}
                          // Headless UI's `role="menuitem"` overrides a `role` prop, and React sets the
                          // attribute only when the prop changes, so a ref keeps the radio role
                          ref={setItemRole}
                          aria-checked={itemsRole ? !!item.active : undefined}
                          onClick={item.onClick}
                          disabled={item.disabled}
                          className={twMerge(
                            MENU_ITEM_BUTTON_CLASS,
                            !item.disabled && "data-[focus]:bg-sidebar-button-active-bg",
                            item.disabled && "opacity-50",
                            item.className,
                          )}
                          {...(item.hintKey ? createHintHandlers(item.hintKey) : {})}
                        >
                          <MenuItemContent item={item} />
                        </button>
                      </MenuItem>
                      {ItemFooter && <ItemFooter open={open} close={close} />}
                    </React.Fragment>
                  );
                })}
                {children}
                {Footer && <Footer open={open} close={close} />}
              </MenuItems>
            ) : null}
          </div>
        );
      }}
    </Menu>
  );
}
